import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Boxes,
  RefreshCw,
  Search
} from 'lucide-react'
import axios from 'axios'

interface KpiData {
  valor_estoque_proprio: number
  valor_estoque_producao: number
  valor_proprio_terceiros: number
  valor_de_terceiros: number
  total_produtos_com_saldo: number
  total_produtos_saldo_negativo: number
  total_romaneios_aguardando: number
  valor_venda_aguardando: number
  romaneios_criticos_30d: number
  romaneios_15_30d: number
  produtos_falta_imediata: number
  produtos_falta_sem_cobertura: number
  total_compras_casadas: number
  casadas_em_risco_prazo: number
}

interface PosicaoItem {
  cdproduto: string
  cdcomercial: string
  dsproduto: string
  cdunid: string
  nmfam: string
  nmlin: string
  nmmarc: string
  cdlocal: string
  saldo_contabil: number
  custo_medio: number
  valor_estoque: number
  qt_comprometida: number
  qt_separada: number
  qt_compras_abertas: number
  saldo_livre: number
}

interface RomaneioItem {
  cnrom: number
  status: string
  cdped: string
  nmcliente: string
  dias_espera: number
  prazo_prometido: string
  situacao_prazo: string
  total_itens: number
  qtde_total_itens: number
  valor_venda_total: number
  custo_estimado_total: number
}

interface VinculoCasado {
  cncomprascasada: number
  cdproduto: string
  cdcomercial: string
  dsproduto: string
  cdunid: string
  cnpedven?: number
  cdped_venda: string
  qtdeven: number
  prazo_venda: string
  nmcliente: string
  cnpedcom?: number
  cdped_compra: string
  qtdecom: number
  prazo_compra: string
  nmfornecedor: string
  saldo_pendente: number
  status_vinculo: string
}

const fmtMoney = (v: number): string =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)

const fmtNumber = (v: number, d = 2): string =>
  new Intl.NumberFormat('pt-BR', { minimumFractionDigits: d, maximumFractionDigits: d }).format(v || 0)

export function EstoqueDashboard() {
  const [searchParams] = useSearchParams()

  const initialCdProduto = searchParams.get('cdproduto') || ''
  const tenantParam = searchParams.get('tenant') || ''

  const [activeTab, setActiveTab] = useState<'VISAO_GERAL' | 'POSICAO' | 'SEPARACAO' | 'NECESSIDADES'>('VISAO_GERAL')
  const [loading, setLoading] = useState(false)
  const [meta, setMeta] = useState<any>(null)
  const [kpis, setKpis] = useState<KpiData | null>(null)
  const [posicaoItems, setPosicaoItems] = useState<PosicaoItem[]>([])
  const [romaneios, setRomaneios] = useState<RomaneioItem[]>([])
  const [casadas, setCasadas] = useState<VinculoCasado[]>([])

  // Filtros
  const [searchFilter, setSearchFilter] = useState(initialCdProduto)
  const [faixaEspera, setFaixaEspera] = useState<string>('')

  const getDsBaseUrl = () => {
    if (typeof window !== 'undefined') {
      const port = window.location.port || ''
      if (['3000', '5173', '5174', '9083', '9084'].includes(port)) {
        return '/ds-api'
      }
      return `${window.location.origin}/api`
    }
    return '/ds-api'
  }

  const getHeaders = () => {
    const token = typeof window !== 'undefined' ? sessionStorage.getItem('access_token') || searchParams.get('token') : null
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    }
  }

  const loadData = async () => {
    setLoading(true)
    const base = getDsBaseUrl()
    const headers = getHeaders()
    const tenantQuery = tenantParam ? `?tenant=${tenantParam}` : ''

    try {
      // 1. Meta
      const metaRes = await axios.get(`${base}/v1/estoque/meta${tenantQuery}`, { headers })
      setMeta(metaRes.data)

      // 2. KPIs
      const kpisRes = await axios.post(`${base}/v1/estoque/kpis`, { tenant: tenantParam || undefined }, { headers })
      setKpis(kpisRes.data?.kpis || null)

      // 3. Posição
      const posRes = await axios.post(
        `${base}/v1/estoque/posicao`,
        {
          cdproduto: searchFilter || undefined,
          cdcomercial: searchFilter || undefined,
          tenant: tenantParam || undefined,
          limit: 100
        },
        { headers }
      )
      setPosicaoItems(posRes.data?.items || [])

      // 4. Romaneios aguardando
      const sepRes = await axios.post(
        `${base}/v1/estoque/separacao-aguardando`,
        {
          faixa_dias_espera: faixaEspera || undefined,
          tenant: tenantParam || undefined,
          limit: 50
        },
        { headers }
      )
      setRomaneios(sepRes.data?.romaneios || [])

      // 5. Necessidades e Casadas
      const necRes = await axios.post(
        `${base}/v1/estoque/necessidades-casadas`,
        {
          tenant: tenantParam || undefined,
          limit: 50
        },
        { headers }
      )
      setCasadas(necRes.data?.vinculos || [])
    } catch (err) {
      console.error('Erro ao carregar dados de estoque:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [tenantParam, faixaEspera])

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-[1600px] mx-auto min-h-screen">
      {/* Header com Nome da Empresa e Ações */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-200 dark:border-slate-800">
        <div>
          <div className="flex items-center gap-2">
            <Boxes className="w-6 h-6 text-purple-600" />
            <h1 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight">
              Dashboard de Estoques e Atendimento
            </h1>
            <span className="px-2 py-0.5 rounded text-[11px] font-black uppercase tracking-wider bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300">
              Galatea IA
            </span>
          </div>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">
            Empresa: <strong className="text-slate-800 dark:text-slate-200">{meta?.empresa?.nome || 'Conectando...'}</strong>
            {meta?.empresa?.cnpj && ` · CNPJ: ${meta.empresa.cnpj}`}
            {meta?.empresa?.banco && ` · Base: ${meta.empresa.banco}`}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={loadData}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors shadow-xs cursor-pointer"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin text-purple-600' : ''}`} />
            <span>Atualizar</span>
          </button>
        </div>
      </div>

      {/* Navegação por Abas */}
      <div className="flex gap-2 border-b border-slate-200 dark:border-slate-800 pb-2 overflow-x-auto">
        {[
          { id: 'VISAO_GERAL', label: 'Visão Geral & Indicadores' },
          { id: 'POSICAO', label: 'Posição de Estoque & Disponibilidade' },
          { id: 'SEPARACAO', label: 'Separado Aguardando Faturamento' },
          { id: 'NECESSIDADES', label: 'Necessidades & Compras Casadas' }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`px-4 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-purple-600 text-white shadow-sm'
                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Aba 1: Visão Geral & KPIs */}
      {activeTab === 'VISAO_GERAL' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Card 1: Valor Próprio */}
            <div className="p-5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Estoque Próprio</span>
              <h2 className="text-2xl font-black text-slate-900 dark:text-white mt-1">
                {fmtMoney(kpis?.valor_estoque_proprio || 0)}
              </h2>
              <div className="mt-2 text-xs text-slate-500 flex items-center justify-between">
                <span>Itens com saldo:</span>
                <span className="font-bold text-slate-800 dark:text-slate-200">{kpis?.total_produtos_com_saldo || 0}</span>
              </div>
            </div>

            {/* Card 2: Separado Aguardando */}
            <div className="p-5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm">
              <span className="text-[11px] font-bold uppercase tracking-wider text-amber-600">Aguardando Faturamento</span>
              <h2 className="text-2xl font-black text-amber-600 mt-1">
                {fmtMoney(kpis?.valor_venda_aguardando || 0)}
              </h2>
              <div className="mt-2 text-xs text-slate-500 flex items-center justify-between">
                <span>Romaneios abertos:</span>
                <span className="font-bold text-amber-700 dark:text-amber-400">
                  {kpis?.total_romaneios_aguardando || 0} ({kpis?.romaneios_criticos_30d || 0} &gt; 30d)
                </span>
              </div>
            </div>

            {/* Card 3: Falta Imediata */}
            <div className="p-5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm">
              <span className="text-[11px] font-bold uppercase tracking-wider text-red-600">Necessidades Críticas</span>
              <h2 className="text-2xl font-black text-red-600 mt-1">
                {kpis?.produtos_falta_imediata || 0}
              </h2>
              <div className="mt-2 text-xs text-slate-500 flex items-center justify-between">
                <span>Sem cobertura compras:</span>
                <span className="font-bold text-red-700 dark:text-red-400">
                  {kpis?.produtos_falta_sem_cobertura || 0} itens
                </span>
              </div>
            </div>

            {/* Card 4: Compras Casadas */}
            <div className="p-5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm">
              <span className="text-[11px] font-bold uppercase tracking-wider text-purple-600">Compras Casadas</span>
              <h2 className="text-2xl font-black text-purple-600 mt-1">
                {kpis?.total_compras_casadas || 0}
              </h2>
              <div className="mt-2 text-xs text-slate-500 flex items-center justify-between">
                <span>Risco de prazo:</span>
                <span className="font-bold text-purple-700 dark:text-purple-400">
                  {kpis?.casadas_em_risco_prazo || 0} vínculos
                </span>
              </div>
            </div>
          </div>

          {/* Destaques de Estoque por Natureza */}
          <div className="p-5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm space-y-4">
            <h3 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-wider">
              Distribuição por Natureza e Localização
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
              <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
                <p className="text-slate-500">Fabricação / Picking Local (14 / 1K)</p>
                <p className="text-lg font-bold text-slate-900 dark:text-white mt-1">
                  {fmtMoney(kpis?.valor_estoque_producao || 0)}
                </p>
              </div>
              <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
                <p className="text-slate-500">Próprio em Terceiros (15)</p>
                <p className="text-lg font-bold text-slate-900 dark:text-white mt-1">
                  {fmtMoney(kpis?.valor_proprio_terceiros || 0)}
                </p>
              </div>
              <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
                <p className="text-slate-500">De Terceiros em Nosso Poder (20)</p>
                <p className="text-lg font-bold text-slate-900 dark:text-white mt-1">
                  {fmtMoney(kpis?.valor_de_terceiros || 0)}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Aba 2: Posição de Estoque */}
      {activeTab === 'POSICAO' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
              <input
                type="text"
                placeholder="Filtrar por código comercial ou PN..."
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && loadData()}
                className="w-full pl-9 pr-3 py-2 text-xs rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
              />
            </div>
            <button
              onClick={loadData}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold rounded-lg cursor-pointer transition-colors"
            >
              Pesquisar
            </button>
          </div>

          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 dark:bg-slate-800/60 uppercase text-[10px] font-bold text-slate-500 tracking-wider border-b border-slate-200 dark:border-slate-700">
                  <tr>
                    <th className="px-4 py-3">Código / PN</th>
                    <th className="px-4 py-3">Descrição</th>
                    <th className="px-4 py-3">Unid</th>
                    <th className="px-4 py-3 text-right">Saldo Contábil</th>
                    <th className="px-4 py-3 text-right">Comprometido</th>
                    <th className="px-4 py-3 text-right">Saldo Livre</th>
                    <th className="px-4 py-3 text-right">Custo Médio</th>
                    <th className="px-4 py-3 text-right">Valor Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {posicaoItems.map((item) => (
                    <tr key={item.cdproduto} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <td className="px-4 py-3 font-mono font-bold text-purple-600 dark:text-purple-400">
                        {item.cdcomercial || item.cdproduto}
                      </td>
                      <td className="px-4 py-3 text-slate-700 dark:text-slate-300 max-w-xs truncate">
                        {item.dsproduto}
                      </td>
                      <td className="px-4 py-3 text-slate-500">{item.cdunid}</td>
                      <td className="px-4 py-3 text-right font-bold text-slate-900 dark:text-white">
                        {fmtNumber(item.saldo_contabil)}
                      </td>
                      <td className="px-4 py-3 text-right text-amber-600 font-semibold">
                        {fmtNumber(item.qt_comprometida)}
                      </td>
                      <td className={`px-4 py-3 text-right font-black ${item.saldo_livre < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                        {fmtNumber(item.saldo_livre)}
                      </td>
                      <td className="px-4 py-3 text-right text-slate-600 dark:text-slate-400">
                        {fmtMoney(item.custo_medio)}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-slate-900 dark:text-white">
                        {fmtMoney(item.valor_estoque)}
                      </td>
                    </tr>
                  ))}
                  {posicaoItems.length === 0 && (
                    <tr>
                      <td colSpan={8} className="p-8 text-center text-slate-400">
                        Nenhum produto encontrado com os filtros aplicados.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Aba 3: Separado Aguardando Faturamento */}
      {activeTab === 'SEPARACAO' && (
        <div className="space-y-4">
          <div className="flex gap-2">
            {[
              { id: '', label: 'Todas as Faixas' },
              { id: 'ATE_7', label: 'Até 7 dias' },
              { id: '8_A_14', label: '8 a 14 dias' },
              { id: '15_A_30', label: '15 a 30 dias' },
              { id: 'MAIS_30', label: 'Mais de 30 dias' }
            ].map((f) => (
              <button
                key={f.id}
                onClick={() => setFaixaEspera(f.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                  faixaEspera === f.id
                    ? 'bg-amber-600 text-white shadow-xs'
                    : 'bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-800'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 dark:bg-slate-800/60 uppercase text-[10px] font-bold text-slate-500 tracking-wider border-b border-slate-200 dark:border-slate-700">
                  <tr>
                    <th className="px-4 py-3">Romaneio</th>
                    <th className="px-4 py-3">Pedido</th>
                    <th className="px-4 py-3">Cliente</th>
                    <th className="px-4 py-3 text-center">Dias Espera</th>
                    <th className="px-4 py-3">Situação Prazo</th>
                    <th className="px-4 py-3 text-right">Itens</th>
                    <th className="px-4 py-3 text-right">Custo Separado</th>
                    <th className="px-4 py-3 text-right">Valor Venda</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {romaneios.map((r) => (
                    <tr key={r.cnrom} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <td className="px-4 py-3 font-mono font-bold text-purple-600 dark:text-purple-400">
                        #{r.cnrom}
                      </td>
                      <td className="px-4 py-3 font-mono font-bold text-slate-800 dark:text-slate-200">
                        {r.cdped || '--'}
                      </td>
                      <td className="px-4 py-3 text-slate-700 dark:text-slate-300 max-w-xs truncate">
                        {r.nmcliente}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-0.5 rounded text-[11px] font-black ${
                          r.dias_espera > 30 ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' :
                          r.dias_espera >= 15 ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' :
                          'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
                        }`}>
                          {r.dias_espera} dias
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-[11px] font-bold ${r.situacao_prazo === 'ATRASADO' ? 'text-red-600' : 'text-emerald-600'}`}>
                          {r.situacao_prazo === 'ATRASADO' ? 'Em Atraso' : 'No Prazo'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-slate-700 dark:text-slate-300 font-semibold">
                        {r.total_itens} ({fmtNumber(r.qtde_total_itens)})
                      </td>
                      <td className="px-4 py-3 text-right text-slate-500">
                        {fmtMoney(r.custo_estimado_total)}
                      </td>
                      <td className="px-4 py-3 text-right font-black text-slate-900 dark:text-white">
                        {fmtMoney(r.valor_venda_total)}
                      </td>
                    </tr>
                  ))}
                  {romaneios.length === 0 && (
                    <tr>
                      <td colSpan={8} className="p-8 text-center text-slate-400">
                        Nenhum romaneio aguardando faturamento para esta seleção.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Aba 4: Necessidades & Compras Casadas */}
      {activeTab === 'NECESSIDADES' && (
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 dark:bg-slate-800/60 uppercase text-[10px] font-bold text-slate-500 tracking-wider border-b border-slate-200 dark:border-slate-700">
                  <tr>
                    <th className="px-4 py-3">Item / Código</th>
                    <th className="px-4 py-3">Venda (Pedido / Cliente)</th>
                    <th className="px-4 py-3 text-center">Prazo Venda</th>
                    <th className="px-4 py-3">Compra (Pedido / Fornecedor)</th>
                    <th className="px-4 py-3 text-center">Prazo Compra</th>
                    <th className="px-4 py-3 text-right">Saldo Aberto</th>
                    <th className="px-4 py-3">Status Cobertura</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {casadas.map((c) => (
                    <tr key={c.cncomprascasada} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <td className="px-4 py-3 font-mono font-bold text-purple-600 dark:text-purple-400">
                        {c.cdcomercial || c.cdproduto}
                      </td>
                      <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                        <strong>#{c.cdped_venda || c.cnpedven}</strong> - {c.nmcliente}
                      </td>
                      <td className="px-4 py-3 text-center text-slate-500">
                        {c.prazo_venda ? new Date(c.prazo_venda).toLocaleDateString('pt-BR') : '--'}
                      </td>
                      <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                        <strong>#{c.cdped_compra || c.cnpedcom}</strong> - {c.nmfornecedor}
                      </td>
                      <td className="px-4 py-3 text-center text-slate-500">
                        {c.prazo_compra ? new Date(c.prazo_compra).toLocaleDateString('pt-BR') : '--'}
                      </td>
                      <td className="px-4 py-3 text-right font-black text-slate-900 dark:text-white">
                        {fmtNumber(c.saldo_pendente)} {c.cdunid}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider ${
                          c.status_vinculo === 'PRAZO_INCOMPATIVEL' ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' :
                          c.status_vinculo === 'COMPRA_CANCELADA' ? 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300' :
                          'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'
                        }`}>
                          {c.status_vinculo}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {casadas.length === 0 && (
                    <tr>
                      <td colSpan={7} className="p-8 text-center text-slate-400">
                        Nenhum vínculo de compras casadas registrado.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
